import { useEffect, useState } from "react";
import CitySEOHead from "@/components/feature/CitySEOHead";
import SpringdaleHeroSection from "./components/HeroSection";
import SpringdaleProcessingTimeSection from "./components/ProcessingTimeSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Springdale, AR?", a: "Our emergency service can have your passport in as little as 24 hours. Expedited processing is 3–5 business days, and standard takes 7–10 business days. With Springdale being the heart of the Tyson Foods operations, we're well-versed in urgent corporate travel needs." },
  { q: "Where is the nearest passport acceptance facility to Springdale?", a: "The Springdale Main Post Office on Emma Avenue is closest. The Fayetteville acceptance facility is also nearby. We'll direct you to whichever has the shortest current wait time." },
  { q: "Can Tyson Foods employees get expedited passports for business travel?", a: "Absolutely. We work frequently with NWA corporate travelers from Tyson Foods, JB Hunt, and their supplier networks. International business travel demands can come with very little notice — that's exactly what our emergency service is designed for." },
  { q: "Do I need an appointment for a passport in Springdale?", a: "Most Springdale-area facilities operate on a walk-in basis. We'll advise you on the best time to visit for minimal wait." },
  { q: "What if my passport expires in 6 months — is that okay for international travel?", a: "Many countries require 6 months of validity beyond your return date. If your passport is expiring soon, we recommend renewal before booking international travel — we can turn it around in 3–5 business days." },
  { q: "Can I use your service for a child passport in Springdale?", a: "Yes. Child passport applications (under 16) require both parents to appear at an acceptance facility. We provide complete guidance on required documents and parent consent forms (DS-3053)." },
  { q: "How much does passport expediting cost from Springdale?", a: "Government fees are $130–$190. Our service fees start at $99 for standard expedited. Emergency processing is available at higher tiers. We guarantee the best price — find a lower rate and we'll match it." },
  { q: "Is Northwest Arkansas Community College included in your service area?", a: "Yes, NWACC students are fully within our service area. We help students with study abroad programs, international competitions, and mission trips throughout the year." },
];

const destinations = [
  { name: "Mexico City", country: "Mexico", flag: "🇲🇽", desc: "Tyson Foods supplier visits and NWA business travel to Mexico's economic capital." },
  { name: "São Paulo", country: "Brazil", flag: "🇧🇷", desc: "Tyson Foods and chicken processing industry international business travel hub." },
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Top spring break and family vacation from the NWA region." },
  { name: "Europe", country: "Various", flag: "🇪🇺", desc: "European summer vacations from Springdale families via XNA connections." },
  { name: "Caribbean Cruises", country: "Various", flag: "🚢", desc: "Galveston cruises are popular with Springdale families — 6 hours away." },
  { name: "Japan", country: "Japan", flag: "🇯🇵", desc: "JB Hunt and Walmart supplier relations travel to Asia." },
];

const facilities = [
  { name: "Springdale Main Post Office", address: "703 W Emma Ave, Springdale, AR 72764", phone: "(479) 756-3161", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "15–30 min", website: "https://tools.usps.com/find-location.htm" },
  { name: "Fayetteville Main Post Office", address: "915 E Township St, Fayetteville, AR 72703", phone: "(479) 442-9234", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "20–40 min", website: "https://tools.usps.com/find-location.htm" },
  { name: "Rogers Post Office", address: "701 W Walnut St, Rogers, AR 72756", phone: "(479) 636-1200", hours: "Mon–Fri 8:30 AM – 4:30 PM", wait: "10–25 min", website: "https://tools.usps.com/find-location.htm" },
];

const testimonials = [
  { name: "Carlos M.", city: "Springdale", stars: 5, text: "Tyson Foods sent me to São Paulo with 10 days notice. Fast Passport Center had my passport renewed in 3 days. Incredible." },
  { name: "Sarah K.", city: "Emma Avenue, Springdale", stars: 5, text: "Our whole family needed passports for a Caribbean cruise. All 5 passports in 6 days. No hassle, no stress." },
  { name: "Pastor James R.", city: "Springdale", stars: 5, text: "Taking a mission group to Guatemala. They handled 8 first-time passports with no issues. Amazing service." },
];

export default function SpringdalePage() {
  return (
    <>
      <CitySEOHead
        city="Springdale"
        state="Arkansas"
        stateAbbr="AR"
        route="/cities/springdale"
        title="Emergency Passport Springdale, AR — Expedited & Fast Passport Service | Fast Passport Center"
        description="Emergency and expedited passport services in Springdale, AR — as fast as 24 hours. Serving Tyson Foods corporate travelers, NWACC students, and NWA families. A+ BBB rated."
        geoPlacename="Springdale, Arkansas"
        geoPosition="36.1867;-94.1288"
        faqs={faqs}
        servicesSchema={[]}
        statePageName="Arkansas"
        statePageRoute="/states/arkansas"
      />
      <div className="font-sans">
        <SpringdaleHeroSection />
        <StickyNav />
        <SpringdaleProcessingTimeSection />
        <ServicesSection />
        <LocalAreaSection />
        <TravelTimingSection />
        <DestinationsSection />
        <TestimonialsSection />
        <AcceptanceFacilitiesSection />
        <ComparisonSection />
        <MistakesSection />
        <HowItWorksSection />
        <FAQSection />
        <HelpfulResourcesSection />
        <section id="vital-records" className="py-16 md:py-24 bg-[#F7F8FA]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Vital Records</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Birth, Death, Marriage &amp; Divorce Records — Washington County</h2>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-3xl mx-auto text-center">
              If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records serving Springdale and Washington County residents. Most records can be ordered online for delivery within 5–10 business days.
            </p>
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {[
                  { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Arkansas Department of Health (ADH) Vital Records. Online, mail, or in-person options available.", url: "https://www.healthy.arkansas.gov/programs-services/program/certificates-and-records" },
                  { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through ADH Vital Records. Required for estate and legal matters.", url: "https://www.healthy.arkansas.gov/programs-services/program/certificates-and-records" },
                  { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Washington County Clerk. Certified copies available for name changes and legal purposes.", url: "https://www.washingtoncountyar.gov/government/departments-a-e/county-clerk" },
                  { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Washington County Circuit Clerk. Required for name change passport corrections and legal documentation.", url: "https://www.washingtoncountyar.gov/government/departments-a-e/county-clerk" },
                ].map((record, i) => (
                  <a key={i} href={record.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3.5 bg-white border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Need records from another Arkansas county?</strong> Visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>
        </section>
        <SEOCrossLinks currentRoute="/cities/springdale" state="AR" />
        <FinalCTA />
      </div>
    </>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) { setActive(navItems[i].href); return; }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {navItems.map((item) => (<a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>))}
        </div>
      </div>
    </div>
  );
}

function ServicesSection() {
  const services = [
    { icon: "ri-time-line", title: "Emergency Passport", desc: "Tyson Foods global trip in 10 days? JB Hunt business meeting in Brazil next week? We prioritize your application with reserved agency slots — as fast as 24 hours.", bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"], badge: "Same-Day Available", link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport" },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Already hold a U.S. passport? We help Springdale and NWA residents renew fast — no agency visit required for most renewals. Perfect for frequent corporate travelers from Tyson, JB Hunt, and their supplier networks.", bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"], badge: "Most Common", link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal" },
    { icon: "ri-passport-line", title: "New Passport", desc: "First-time applicants require an in-person acceptance clerk visit. We guide you through every step with clear instructions for Springdale and NWA residents — including Marshallese community members applying for naturalization.", bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=new-passport" },
    { icon: "ri-search-eye-line", title: "Lost or Stolen Passport", desc: "Replacements require additional forms. We guide you through exactly what is needed to avoid delays before your next international trip.", bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport" },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists for Springdale families planning international vacations, mission trips, and family reunions.", bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=child-passport" },
    { icon: "ri-edit-line", title: "Name Change", desc: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations.", bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=name-change" },
    { icon: "ri-file-damage-line", title: "Damaged Passport", desc: "Replacing a water-damaged, torn, or otherwise compromised passport is faster than you think. We guide you through exactly what is needed to avoid delays before your next international trip.", bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport" },
    { icon: "ri-bank-card-line", title: "Passport Card", desc: "Wallet-sized U.S. Passport Card for land and sea travel to Canada, Mexico, Bermuda, and the Caribbean.", bullets: ["REAL ID compliant alternative", "Fits in your wallet", "Land/sea border crossing approved"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=passport-card" },
    { icon: "ri-file-copy-line", title: "Second Passport", desc: "Obtain a second valid U.S. passport for frequent international travelers who need overlapping visas. Ideal for NWA corporate executives juggling multiple international engagements.", bullets: ["For frequent business travelers", "Overlapping visa support", "Expedited processing available"], badge: null, link: "https://fastpassportcenter.com/checkout/?passport=second-passport" },
  ];
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Springdale &amp; NWA</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="group relative bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col">
              {s.badge && <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">{s.badge}</span>}
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
              <ul className="space-y-1.5 mb-6 flex-1">
                {s.bullets.map((b: string, j: number) => <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{b}</li>)}
              </ul>
              <a href={s.link} className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">Get Started <i className="ri-arrow-right-line"></i></a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Springdale, Arkansas</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Springdale, Arkansas is the core of Northwest Arkansas's industrial powerhouse. Home to Tyson Foods' world headquarters, the city generates an extraordinary volume of international business travel — to Brazil, Mexico, China, Japan, and Europe — as Tyson's global protein supply chain spans dozens of countries. Corporate buyers, quality assurance managers, and executive leadership regularly travel internationally on short notice, making expedited passport services a business necessity rather than a luxury.</p>
          <p>Beyond Tyson, Springdale hosts a vibrant and fast-growing population. The city's significant Marshallese and Hispanic communities have created a diverse travel landscape, with many residents maintaining strong family ties to the Marshall Islands, Mexico, and Central America. The Marshallese community in particular is one of the largest in the continental United States, and many residents need U.S. passports for the first time or for renewal as they naturalize as U.S. citizens.</p>
          <p>Northwest Arkansas Community College (NWACC), located in nearby Bentonville, serves over 7,000 students with growing international exchange and study abroad programs. Springdale students participate in NWACC's European summer programs and exchange partnerships with universities in Latin America and Asia, creating seasonal passport demand spikes around semester starts.</p>
          <p>The faith community in Springdale is also notably active in international mission work. Churches routinely organize mission trips to Guatemala, Costa Rica, Jamaica, and Africa — often requiring first-time passports for adult members who have never traveled internationally. We help organize group passport processing for mission teams, coordinating multiple applications simultaneously to ensure everyone is travel-ready by departure day.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Springdale Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { month: "Year-Round", title: "Corporate Travel", desc: "Tyson Foods and NWA corporate international travel needs are constant and urgent." },
            { month: "March–April", title: "Spring Break", desc: "Springdale families head to Cancun and Mexico. Mission trip season peaks." },
            { month: "June–August", title: "Summer Missions", desc: "Church mission trips to Guatemala, Costa Rica, and Jamaica. Group processing." },
            { month: "August–September", title: "Study Abroad", desc: "NWACC and UA student semester departures for international programs." },
            { month: "October–November", title: "Renewal Season", desc: "Ideal time to renew before the holiday travel rush." },
            { month: "December–January", title: "Holiday Travel", desc: "International family reunions and winter cruise departures." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Springdale Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Springdale Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Springdale</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Website</p><p className="text-[#1A365D] text-sm font-medium"><a href={f.website} target="_blank" rel="nofollow noopener noreferrer" className="text-[#1A365D] font-medium hover:text-[#F6C344] transition-colors underline underline-offset-2">Visit website</a></p></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
    { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
    { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
    { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
    { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
    { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
    { feature: "Group Processing", us: "Available for mission trips and teams", them: "Not available" },
    { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  ];
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="mb-8 bg-amber-50 border border-[#F6C344]/40 rounded-lg p-6">
          <h3 className="font-serif text-lg font-bold text-[#1A365D] mb-3">
            Springdale Passport Agency Appointment Not Available?
          </h3>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Regional passport agency appointments are routinely booked 2–4 weeks out — and walk-ins are never accepted. If you searched for an appointment and came up empty, Fast Passport Center's registered courier channel is your fastest next step. No appointment. No waiting in line at the federal building. Processing as fast as 2–3 business days.
          </p>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all hover:scale-105 whitespace-nowrap"
          >
            Get Started <i className="ri-arrow-right-line"></i>
          </a>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  const mistakes = [
    { title: "Corporate Booking Before Renewal", desc: "NWA business travelers often book international flights before checking passport expiration. Always verify before booking." },
    { title: "Wrong Photo", desc: "Photos taken at stores near corporate HQs are frequently rejected. We verify photo specs in advance." },
    { title: "Mission Trip Last Minute", desc: "Churches often start passport processing 4 weeks before a mission trip — not enough for first-time applicants." },
    { title: "Missing Documents", desc: "Birth certificates without raised seals are rejected. We verify all documents before you visit the facility." },
    { title: "Form Confusion", desc: "Choosing DS-11 vs DS-82 incorrectly causes major delays. We identify the correct form immediately." },
  ];
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Springdale Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Emergency, expedited, or standard based on departure date." },
    { num: "02", title: "Complete Application", desc: "We walk you through DS-11 or DS-82 step by step." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Springdale/NWA facility with shortest wait." },
    { num: "04", title: "We Handle Processing", desc: "Hand-carried to the State Department for priority processing." },
    { num: "05", title: "Receive Your Passport", desc: "Secure delivery with real-time tracking." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Springdale Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Springdale Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "XNA Airport", desc: "Northwest Arkansas National Airport — international connections.", link: "https://www.flyxna.com/" },
            { title: "CDC Travel Health", desc: "Health recommendations for international destinations.", link: "https://wwwnc.cdc.gov/travel" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Springdale?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">Corporate traveler, mission team leader, or family planning an adventure — we'll get your passport ready fast.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}